import { useCallback, useContext, useEffect, useState } from "react";
import {
  addNotificationsfromPaginateToLocalStorage,
  addNotificationToLocalStorage,
  getNotificationsFromLocalStorage,
  loadNotificationsByUser,
  markNotificationAsRead,
  markNotificationsAsRead,
  renderNotification,
} from "./helper";
import { DisclosureBell, MenuBell } from "../buttons/navigation/MenuBell";
import { useWebSocketClient } from "../../context/websocketClient";
import notificationSound from "../../public/sounds/service-bell-sound.wav";
import "./Notifications.css";
import { NotificationsService } from "../../services/notifications/notifications.service";
import { notificationConfigs } from "../../utils/notificationConfigs";
import { NotificationsContext } from "../../context/Disclosures/notifications/notificationsContext";
import { RewardsViewContext } from "../../context/Disclosures/rewards/rewardsContext";

export const Notifications = ({ device }) => {
  const { updateRewardsView, rewardsViewData } = useContext(RewardsViewContext);
  const { updateNotificationsData } = useContext(NotificationsContext);

  const [notifications, setNotifications] = useState([]);
  const [hasUpdatedNotifications, setHasUpdatedNotifications] = useState(false);
  const [iconEffect, setIconEffect] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(false);
  const [page, setPage] = useState(2);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const handleSetUnreadNotifications = useCallback(
    (_notifications = []) => {
      const unreadNotifications = _notifications.some(
        (noti) => noti.read === false,
      );

      updateNotificationsData({
        unreadNotifications,
      });

      setUnreadNotifications(unreadNotifications);
    },
    [updateNotificationsData],
  );

  const handleGetNotifications = useCallback(() => {
    const storedNotifications = getNotificationsFromLocalStorage();

    if (Array.isArray(storedNotifications) && storedNotifications.length > 0) {
      const renderedNotifications = storedNotifications.map(renderNotification);

      handleSetUnreadNotifications(renderedNotifications);
      setNotifications(renderedNotifications);
    }
  }, [handleSetUnreadNotifications]);

  const handleOpenDisclosureMyRewards = () => {
    updateRewardsView({
      ...rewardsViewData,
      openDisclosure: true,
      openDisclosureMyRewards: true,
    });
  };

  const handleUpdateReadNotification = async (notificationId, read, type) => {
    markNotificationAsRead(notificationId);
    setHasUpdatedNotifications(true);
    handleGetNotifications();

    if (read === true) {
      await NotificationsService.UpdateNotification(notificationId, {
        read: true,
      });
    }

    if (type === "level_up") {
      handleOpenDisclosureMyRewards();
    }
  };

  const handleBulkUpdateNotification = useCallback(async () => {
    setUnreadNotifications(false);

    const notificationsId = notifications
      .filter((noti) => !noti.read)
      .map((noti) => noti.id);

    if (!notificationsId.length) {
      return;
    }

    markNotificationsAsRead(notificationsId);
    setHasUpdatedNotifications(true);
    handleGetNotifications();

    await NotificationsService.bulkUpdateNotification(notificationsId, true);
  }, [notifications, handleGetNotifications]);

  
  const handler = {
    notification: (data) => {
      addNotificationToLocalStorage(data);
      handleGetNotifications();
      setHasUpdatedNotifications(true);
      const audio = new Audio(notificationSound);
      audio.play();
      setTimeout(() => setIconEffect(true), 2000);
      setTimeout(() => setIconEffect(false), 4000);
    },
  };

  useWebSocketClient({ userId: localStorage.getItem("doc"), handler });

  const handleLoadMoreNotifications = useCallback(async () => {
    if (isLoading) return;
    if (!hasMore) return;
    setIsLoading(true);
    let response = await loadNotificationsByUser(
      localStorage.getItem("doc"),
      page,
      notificationConfigs.initNotificationsRows,
    );
    let notifications = response?.notifications ?? [];
    if (notifications.length > 0) {
      setHasMore(true);
      addNotificationsfromPaginateToLocalStorage(notifications);
      setPage((prevPage) => prevPage + 1);
      setIsLoading(false);
      handleGetNotifications();
    } else {
      setIsLoading(false);
      setHasMore(false);
    }
  }, [page, isLoading, hasMore, handleGetNotifications]);

  useEffect(() => {
    const storedNotifications = getNotificationsFromLocalStorage();
    if (Array.isArray(storedNotifications) && storedNotifications.length > 0) {
      let renderedNotifications = storedNotifications.map(renderNotification);
      handleSetUnreadNotifications(renderedNotifications);
      setNotifications(renderedNotifications);
    }
  }, [hasUpdatedNotifications, handleSetUnreadNotifications]);

  return (
    <>
      {device === "desktop" && (
        <>
          <MenuBell
            noti={notifications}
            iconEffect={iconEffect}
            handleUpdateReadNotification={handleUpdateReadNotification}
            handleLoadMoreNotifications={handleLoadMoreNotifications}
            isLoading={isLoading}
            hasMore={hasMore}
            handleBulkUpdateNotification={handleBulkUpdateNotification}
          ></MenuBell>
          {unreadNotifications && (
            <div
              className={`absolute w-3 h-3 bg-red rounded-full right-0 top-2 border-2 border-purpleLight`}
            ></div>
          )}
        </>
      )}
      {device !== "desktop" && (
        <>
          <DisclosureBell
            noti={notifications}
            iconEffect={iconEffect}
            handleUpdateReadNotification={handleUpdateReadNotification}
            handleLoadMoreNotifications={handleLoadMoreNotifications}
            isLoading={isLoading}
            hasMore={hasMore}
            handleBulkUpdateNotification={handleBulkUpdateNotification}
          ></DisclosureBell>
          {unreadNotifications && (
            <div
              className={`absolute w-3 h-3 bg-red rounded-full left-2 top-1 border-2 border-purpleLight`}
            ></div>
          )}
        </>
      )}
    </>
  );
};
